'use client'
import { usePlanFeatures } from "@/hooks/use-plan-features"

import { useCreditFormatters } from "@/hooks/use-credit-formatters"


import { CreditCard, ExternalLink, Info, Loader2, ShieldAlert } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useUpgradePaymentMethodQuery } from '@/hooks/queries/billing-payment-method'
import type { BillingPlan, PaymentMethodSummary } from '@/lib/billing/types'

import { formatCentsAsUsd, formatMonthlyPlanPrice } from '@/lib/billing/utils/format'
import { useTranslations } from "next-intl"



export interface StripePlanChange {
  plan: BillingPlan
  direction: 'upgrade' | 'downgrade'
}

interface StripePlanChangeDialogProps {
  change: StripePlanChange | null
  currentPlanName: string | null
  /** The current period end as the Plan card shows it, e.g. "Oct 24". */
  periodEndLabel: string | null
  /** When the current plan's payment credits expire, e.g. "Oct 25, 2026"; null when they do not. */
  currentPlanCreditsUntil: string | null
  submitting: boolean
  /** Stripe accepted the change, but the plan has not flipped within the wait budget. */
  syncTimedOut: boolean
  onConfirm: (plan: BillingPlan) => void
  onClose: () => void
  /** Opens the Stripe Customer Portal to add or swap the card. */
  onManageCard: () => void
}

/** Confirms an in-place Stripe plan change and states what happens to money and credits. */
export function StripePlanChangeDialog({
  change,
  currentPlanName,
  periodEndLabel,
  currentPlanCreditsUntil,
  submitting,
  syncTimedOut,
  onConfirm,
  onClose,
  onManageCard,
}: StripePlanChangeDialogProps) {
  const { data: paymentMethod } = useUpgradePaymentMethodQuery(change?.direction === 'upgrade' ? change.plan.id : null)

  return (
    <AlertDialog open={change !== null} onOpenChange={(open) => { if (!open) onClose() }}>
      <AlertDialogContent className={submitting ? 'cursor-wait' : ''}>
        {change && (
          <ChangeDetails
            change={change}
            currentPlanName={currentPlanName}
            periodEndLabel={periodEndLabel}
            currentPlanCreditsUntil={currentPlanCreditsUntil}
            submitting={submitting}
            syncTimedOut={syncTimedOut}
            paymentMethod={paymentMethod}
            onConfirm={onConfirm}
            onManageCard={onManageCard}
          />
        )}
      </AlertDialogContent>
    </AlertDialog>
  )
}

function ChangeDetails({
  change,
  currentPlanName,
  periodEndLabel,
  currentPlanCreditsUntil,
  submitting,
  syncTimedOut,
  paymentMethod,
  onConfirm,
  onManageCard,
}: Omit<StripePlanChangeDialogProps, 'change' | 'onClose'> & {
  change: StripePlanChange
  paymentMethod: PaymentMethodSummary | null | undefined
}) {
  const { getPlanCreditsText } = usePlanFeatures()

  const { formatCredits } = useCreditFormatters()


  const tUi = useTranslations("Billing.BillingStripePlanChangeDialog")
  const tCommonUi = useTranslations("Common")

  const { plan: target, direction } = change
  const isUpgrade = direction === 'upgrade'
  const planCredits = getPlanCreditsText(target)

  return (
    <>
      <AlertDialogHeader className={`transition-opacity ${submitting ? 'opacity-50' : ''}`}>
        <AlertDialogTitle>{dialogTitle(change, tUi)}</AlertDialogTitle>
        <AlertDialogDescription>
          {dialogDescription(change, currentPlanName, periodEndLabel, keptCreditsText(currentPlanName, currentPlanCreditsUntil, tUi), tUi, getPlanCreditsText)}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <div className={`space-y-2 py-2 text-sm transition-opacity ${submitting ? 'opacity-50 pointer-events-none' : ''}`}>
        {isUpgrade && (
          <>
            <div className="flex justify-between gap-3">
              <span><span key={tUi("chargeToYourCardNow")}>{tUi("chargeToYourCardNow")}</span></span>
              <span className="font-medium">{formatCentsAsUsd(target.price_monthly_cents)}</span>
            </div>
            <SavedCardLine paymentMethod={paymentMethod} onManageCard={onManageCard} />
          </>
        )}
        {isUpgrade && planCredits && (
          <div className="flex justify-between gap-3">
            <span><span key={tUi("creditsWithThisPayment")}>{tUi("creditsWithThisPayment")}</span></span>
            <span className="font-medium text-right">{planCredits}</span>
          </div>
        )}
        {/* A switch to Free grants nothing: the tenant got Free's starting credits at registration. */}
        {target.price_monthly_cents > 0 && target.initial_balance_microusd > 0 && (
          <p className="pt-1 text-xs text-muted-foreground">
            <span>{<span key={tUi("movingToValue0ForTheFirstTimeAlso", { value0: String(target.display_name), value1: String(formatCredits(target.initial_balance_microusd)) })}>{tUi("movingToValue0ForTheFirstTimeAlso", { value0: String(target.display_name), value1: String(formatCredits(target.initial_balance_microusd)) })}</span>}</span>
          </p>
        )}
      </div>
      {(submitting || syncTimedOut) && (
        <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-2 py-1.5 text-xs text-foreground">
          {submitting ? (
            <>
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
              <span><span key={tUi("applyingPlanChangeWaitingForStripeToConfirm")}>{tUi("applyingPlanChangeWaitingForStripeToConfirm")}</span></span>
            </>
          ) : (
            <>
              <Info className="h-3.5 w-3.5 shrink-0" />
              <span><span key={tUi("stripeAcceptedTheChangeThePlanNameMay")}>{tUi("stripeAcceptedTheChangeThePlanNameMay")}</span></span>
            </>
          )}
        </div>
      )}
      <AlertDialogFooter>
        <AlertDialogCancel disabled={submitting}><span key={syncTimedOut ? tUi("close") : tCommonUi("cancel")}>{syncTimedOut ? tUi("close") : tCommonUi("cancel")}</span></AlertDialogCancel>
        {!syncTimedOut && (
          <ConfirmAction
            change={change}
            paymentMethod={paymentMethod}
            submitting={submitting}
            onConfirm={onConfirm}
            onManageCard={onManageCard}
          />
        )}
      </AlertDialogFooter>
    </>
  )
}

function dialogTitle({ plan, direction }: StripePlanChange, tUi: ReturnType<typeof useTranslations<"Billing.BillingStripePlanChangeDialog">>): string {
  if (direction === 'upgrade') return tUi("confirmUpgrade", {plan: plan.display_name})
  return plan.price_monthly_cents === 0
    ? tUi("switchAtPeriodEnd", {plan: plan.display_name})
    : tUi("confirmDowngrade", {plan: plan.display_name})
}

function dialogDescription(
  { plan, direction }: StripePlanChange,
  currentPlanName: string | null,
  periodEndLabel: string | null,
  keptCredits: string,
  tUi: ReturnType<typeof useTranslations<"Billing.BillingStripePlanChangeDialog">>,
  getPlanCreditsText: ReturnType<typeof usePlanFeatures>["getPlanCreditsText"],
): string {
  if (direction === 'upgrade') {
    return tUi("upgradeDescription", {plan: plan.display_name, keptCredits})
  }
  if (plan.price_monthly_cents === 0) {
    const current = currentPlanName ?? tUi("currentPlanDefault")
    const until = periodEndLabel ?? tUi("periodEndDefault")
    return tUi("freeDescription", {current, until, plan: plan.display_name, keptCredits})
  }
  const from = periodEndLabel ? tUi("fromDate", {date: periodEndLabel}) : tUi("fromNextInvoice")
  const credits = getPlanCreditsText(plan)
  const price = formatMonthlyPlanPrice(plan.price_monthly_cents)
  return credits
    ? tUi("paidDescriptionWithCredits", {from, price, credits, keptCredits})
    : tUi("paidDescription", {from, price, keptCredits})
}

function keptCreditsText(currentPlanName: string | null, currentPlanCreditsUntil: string | null, tUi: ReturnType<typeof useTranslations<"Billing.BillingStripePlanChangeDialog">>): string {
  if (currentPlanName && currentPlanCreditsUntil) return tUi("keptPlanCredits", {plan: currentPlanName, until: currentPlanCreditsUntil})
  return tUi("keptCredits")
}

function SavedCardLine({
  paymentMethod,
  onManageCard,
}: {
  paymentMethod: PaymentMethodSummary | null | undefined
  onManageCard: () => void
}) {

  const tUi = useTranslations("Billing.BillingStripePlanChangeDialog")

  if (paymentMethod === undefined) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span><span key={tUi("loadingPaymentMethod")}>{tUi("loadingPaymentMethod")}</span></span>
      </div>
    )
  }
  if (paymentMethod === null) {
    return (
      <div className="flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-destructive">
        <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span><span key={tUi("noCardOnFileAddACardTo")}>{tUi("noCardOnFileAddACardTo")}</span></span>
      </div>
    )
  }
  const expiry = `${String(paymentMethod.expMonth).padStart(2, '0')}/${String(paymentMethod.expYear).slice(-2)}`
  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 ${paymentMethod.isExpired ? 'text-destructive' : 'text-muted-foreground'}`}>
      <span className="flex items-center gap-1.5">
        <CreditCard className="h-3.5 w-3.5" />
        <span className="capitalize">{paymentMethod.brand}</span>
        <span>{<span key={tUi("value0", { value0: String(paymentMethod.last4) })}>{tUi("value0", { value0: String(paymentMethod.last4) })}</span>}</span>
        <span key={paymentMethod.isExpired ? tUi("expiredValue0", { value0: String(expiry) }) : tUi("expValue0", { value0: String(expiry) })}>{paymentMethod.isExpired ? tUi("expiredValue0", { value0: String(expiry) }) : tUi("expValue0", { value0: String(expiry) })}</span>
      </span>
      <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={onManageCard}>
        <span key={paymentMethod.isExpired ? tUi("updateCard") : tUi("useADifferentCard")}>{paymentMethod.isExpired ? tUi("updateCard") : tUi("useADifferentCard")}</span>
      </Button>
    </div>
  )
}

/** Upgrades wait for the card they will charge and never charge an expired one; without a card, "Add card". */
function ConfirmAction({
  change: { plan, direction },
  paymentMethod,
  submitting,
  onConfirm,
  onManageCard,
}: {
  change: StripePlanChange
  paymentMethod: PaymentMethodSummary | null | undefined
  submitting: boolean
  onConfirm: (plan: BillingPlan) => void
  onManageCard: () => void
}) {
  const tUi = useTranslations("Billing.BillingStripePlanChangeDialog")

  const isUpgrade = direction === 'upgrade'
  if (isUpgrade && paymentMethod === null) {
    return (
      <AlertDialogAction onClick={onManageCard}>
        <span><span key={tUi("addCard")}>{tUi("addCard")}</span></span>
        <ExternalLink className="h-3.5 w-3.5 ml-1" />
      </AlertDialogAction>
    )
  }
  return (
    <AlertDialogAction
      // Radix closes the dialog on the primary action; the page closes it once the plan settles.
      onClick={(event) => { event.preventDefault(); onConfirm(plan) }}
      disabled={submitting || (isUpgrade && (!paymentMethod || paymentMethod.isExpired))}
    >
      {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
      <span key={isUpgrade ? tUi("confirmAndPay") : tUi("confirm")}>{isUpgrade ? tUi("confirmAndPay") : tUi("confirm")}</span>
    </AlertDialogAction>
  )
}
